import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { isOwnerOrAdmin } from "@/lib/routeHome";
import i18n from "@/i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/auth/me")
            .then(async (r) => {
                const { registerOfflineUser, saveOfflineSession } = await import("@/lib/offlineSync");
                await registerOfflineUser(r.data.id);
                await saveOfflineSession(r.data);
                setUser(r.data);
            })
            .catch(async (requestError) => {
                if (requestError?.response) {
                    setUser(false);
                    return;
                }
                const { loadOfflineSession } = await import("@/lib/offlineSync");
                setUser((await loadOfflineSession()) || false);
            });
    }, []);

    useEffect(() => {
        if (!user || user === false) return;
        const language = ["en", "tl"].includes(user.language) ? user.language : "en";
        document.documentElement.lang = language;
        i18n.changeLanguage(language).catch(() => null);
    }, [user]);

   const login = async (email, password) => {
  setError("");

  try {
    const { data } = await api.post("/auth/login", {
      email,
      password,
    });

    const { registerOfflineUser, saveOfflineSession } = await import("@/lib/offlineSync");
    await registerOfflineUser(data.user.id);
    await saveOfflineSession(data.user);
    setUser(data.user);
    return data.user;
  } catch (e) {
    setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    return null;
  }
};

    const register = async (payload) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", payload);
            setUser(data);
        return data;
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        return null;
        }
    };

const logout = async () => {
  const authenticatedUserId = user && user !== false ? user.id : null;
  try {
    await api.post("/auth/logout");
  } finally {
    if (authenticatedUserId) {
      try {
        const { clearOfflineDataForUser } = await import("@/lib/offlineSync");
        await clearOfflineDataForUser(authenticatedUserId);
      } catch (error) {
        console.warn("Offline device data could not be cleared cleanly", error);
      }
    }
    setUser(false);
  }
};
    const clearAuthState = () => {
        setUser(false);
        setError("");
    };
    const can = (perm) => {
        if (!user) return false;
        const p = user.role_perms || {};
        return !!(p.all || p[perm]);
    };

    const isOwner = () => isOwnerOrAdmin(user);

    const updateUser = (patch) => {
        setUser((current) => current && current !== false ? { ...current, ...patch } : current);
    };

    return (
        <AuthContext.Provider value={{ user, error, login, register, logout, clearAuthState, can, isOwner, setError, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
